import React, { useState } from 'react';
import { Screen, AppState } from '../App';
import { User, getPendingCheckIns, saveStrategyAttempt, getStudentById, saveStudent, generateId, Win } from '../storage';
import { getNextStrategy } from '../api';
import { containsRiskLanguage } from '../safety';
import SafetyInterstitial from '../components/SafetyInterstitial';
import AiDisclaimer from '../components/AiDisclaimer';

interface Props { navigate: (s: Screen, state?: AppState) => void; user: User | null; }

export default function StrategyCheckIn({ navigate, user }: Props) {
  const pending = user ? getPendingCheckIns(user.id) : [];
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [nextStrategy, setNextStrategy] = useState('');
  const [outcome, setOutcome] = useState('');
  const [safety, setSafety] = useState(false);

  if (pending.length === 0) {
    return (
      <div style={{ padding: '40px 24px', textAlign: 'center' }}>
        <p style={{ fontSize: 40, marginBottom: 16 }}>✅</p>
        <h2 style={{ marginBottom: 12 }}>All caught up!</h2>
        <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>No strategy check-ins pending right now.</p>
        <button onClick={() => navigate('dashboard')} className="btn-primary">Back to Dashboard</button>
      </div>
    );
  }

  const attempt = pending[current];
  const student = user ? getStudentById(user.id, attempt.studentId) : null;

  const handleOutcome = async (result: 'worked' | 'partially' | 'didnt-work') => {
    if (!user) return;
    setOutcome(result);
    attempt.status = result;

    if (result === 'worked') {
      // Log as a win
      const student = getStudentById(user.id, attempt.studentId);
      if (student) {
        const win: Win = { id: generateId(), studentId: student.id, date: Date.now(), description: `Strategy worked: ${attempt.strategy.substring(0, 80)}` };
        student.wins.push(win);
        if (student.wins.length >= 3) student.trend = 'improving';
        saveStudent(user.id, student);
      }
      saveStrategyAttempt(user.id, attempt);
    } else {
      // Risk-language short-circuit before any API call: route to human help.
      if (containsRiskLanguage(`${attempt.strategy} ${attempt.conversationTopic}`)) { setSafety(true); return; }
      // Get next strategy suggestion
      setLoading(true);
      const next = await getNextStrategy(attempt.strategy, result === 'partially' ? 'partially worked' : 'did not work', attempt.conversationTopic);
      setNextStrategy(next);
      attempt.nextStrategy = next;
      saveStrategyAttempt(user.id, attempt);
      setLoading(false);
    }
  };

  const goNext = () => {
    if (current < pending.length - 1) {
      setCurrent(current + 1);
      setOutcome('');
      setNextStrategy('');
    } else {
      navigate('dashboard');
    }
  };

  if (safety) return (
    <div>
      <div className="top-bar">
        <button onClick={() => navigate('dashboard')} style={{ background: 'none', color: 'var(--teal)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><polyline points="15,18 9,12 15,6" /></svg>Back
        </button>
        <span className="top-bar-logo" style={{ fontSize: 15 }}>Get help now</span>
        <div style={{ width: 40 }} />
      </div>
      <SafetyInterstitial onBack={() => { setSafety(false); setOutcome(''); }} />
    </div>
  );

  return (
    <div>
      <div className="top-bar">
        <button onClick={() => navigate('dashboard')} style={{ background: 'none', color: 'var(--teal)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><polyline points="15,18 9,12 15,6" /></svg>Back
        </button>
        <span className="top-bar-logo" style={{ fontSize: 15 }}>Strategy Check-In</span>
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{current + 1}/{pending.length}</span>
      </div>

      <div className="page-content">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <span className="mono" style={{ fontSize: 22, color: 'var(--teal)', fontWeight: 500 }}>{student?.initials || '?'}</span>
          <span className="badge badge-teal">{student?.grade}</span>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Strategy check-in</span>
        </div>

        <div style={{ background: 'var(--teal-light)', borderRadius: 12, padding: '16px 20px', marginBottom: 24 }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--teal)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Strategy you tried</p>
          <p style={{ fontSize: 15, lineHeight: 1.6 }}>{attempt.strategy}</p>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8 }}>Started {new Date(attempt.dateStarted).toLocaleDateString()}</p>
        </div>

        {!outcome && (
          <>
            <h2 style={{ marginBottom: 8 }}>How did it go?</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 20 }}>Be honest — this helps build a better picture of what works for {student?.initials}.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button onClick={() => handleOutcome('worked')} style={{ background: 'var(--green)', color: 'white', padding: '18px 20px', borderRadius: 12, fontSize: 16, fontWeight: 600, border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                ✅ It worked!<br /><span style={{ fontSize: 13, fontWeight: 400, opacity: 0.9 }}>This strategy made a positive difference</span>
              </button>
              <button onClick={() => handleOutcome('partially')} style={{ background: 'var(--amber)', color: 'white', padding: '18px 20px', borderRadius: 12, fontSize: 16, fontWeight: 600, border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                🔄 Partially worked<br /><span style={{ fontSize: 13, fontWeight: 400, opacity: 0.9 }}>Some improvement but not consistent</span>
              </button>
              <button onClick={() => handleOutcome('didnt-work')} style={{ background: 'var(--coral)', color: 'white', padding: '18px 20px', borderRadius: 12, fontSize: 16, fontWeight: 600, border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                ❌ Didn't work<br /><span style={{ fontSize: 13, fontWeight: 400, opacity: 0.9 }}>No change or things got harder</span>
              </button>
            </div>
          </>
        )}

        {outcome === 'worked' && (
          <div className="animate-in">
            <div style={{ background: 'var(--green-light)', borderRadius: 14, padding: '24px', textAlign: 'center', marginBottom: 20 }}>
              <p style={{ fontSize: 40, marginBottom: 12 }}>🎉</p>
              <h2 style={{ color: 'var(--green)', marginBottom: 8 }}>That's a win!</h2>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6 }}>This strategy has been logged as a win in {student?.initials}'s profile. You are building a picture of what works for this student.</p>
            </div>
            <button onClick={goNext} className="btn-primary">{current < pending.length - 1 ? 'Next Check-In' : 'Back to Dashboard'}</button>
          </div>
        )}

        {(outcome === 'partially' || outcome === 'didnt-work') && (
          <div className="animate-in">
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div className="spinner" />
                <p style={{ color: 'var(--teal)', fontWeight: 600, marginTop: 16 }}>Finding what to try next...</p>
              </div>
            ) : nextStrategy ? (
              <>
                <div style={{ background: 'var(--amber-light)', borderRadius: 12, padding: '16px', marginBottom: 16 }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: '#b87800', textTransform: 'uppercase', marginBottom: 6 }}>
                    {outcome === 'partially' ? 'Building on what partially worked' : "Here's what to try next"}
                  </p>
                  <p style={{ fontSize: 15, lineHeight: 1.7 }}>{nextStrategy}</p>
                </div>
                <div style={{ background: 'var(--bg)', borderRadius: 10, padding: '14px', marginBottom: 20, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  💡 This has been saved to {student?.initials}'s profile. You will get another check-in in 3 days.
                </div>
                <button onClick={goNext} className="btn-primary">{current < pending.length - 1 ? 'Next Check-In' : 'Back to Dashboard'}</button>
                <AiDisclaimer navigate={navigate} />
              </>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
